/**
 * InvoiceViewAddresses
 *
 * Renders the party section: Bill To / Buyer box, plus optional Ship To / Consignee box.
 */

import { getCountryName, getStateName } from './invoice-view-helpers'

import type { InvoiceLegacyFields } from './invoice-view-helpers'
import type { Address, Invoice } from '../../../types/billing'

interface AddressBlockProps {
  address: Address
}

interface CityStatePostalCountryInput {
  city?: string
  state?: string
  postalCode?: string
  country?: string
}

function buildStateName(addr: CityStatePostalCountryInput): string {
  if (!addr.state) { return '' }
  const prefix = addr.city ? ', ' : ''
  return `${prefix}${getStateName(addr.state)}`
}

function buildPostalCodeSegment(addr: CityStatePostalCountryInput): string {
  if (!addr.postalCode) { return '' }
  const prefix = (addr.city ?? addr.state) ? ' ' : ''
  return `${prefix}${addr.postalCode}`
}

function buildCountrySegment(addr: CityStatePostalCountryInput): string {
  if (!addr.country) { return '' }
  const hasPrevious = Boolean(addr.city) || Boolean(addr.state) || Boolean(addr.postalCode)
  const prefix = hasPrevious ? ', ' : ''
  return `${prefix}${getCountryName(addr.country)}`
}

function buildCityStateLine(addr: CityStatePostalCountryInput): string {
  const city = addr.city ?? ''
  return `${city}${buildStateName(addr)}${buildPostalCodeSegment(addr)}${buildCountrySegment(addr)}`
}

function shouldRenderCityStateLine(addr: CityStatePostalCountryInput): boolean {
  return Boolean(addr.city) || Boolean(addr.state) || Boolean(addr.postalCode) || Boolean(addr.country)
}

export function AddressBlock({ address }: AddressBlockProps): JSX.Element {
  return (
    <>
      {address.line1 && <div>{address.line1}</div>}
      {address.line2 && <div>{address.line2}</div>}
      {shouldRenderCityStateLine(address) && <div>{buildCityStateLine(address)}</div>}
    </>
  )
}

interface BillToBoxProps {
  invoice: Invoice
  isVendor: boolean
  sellerAddress: Address | null
  partyBillingAddress: Address | null
}

function BillToBox({ invoice, isVendor, sellerAddress, partyBillingAddress }: BillToBoxProps): JSX.Element {
  const displayAddress = isVendor ? sellerAddress : partyBillingAddress
  const gstin = isVendor ? invoice.sellerGstin : invoice.partyGstin
  // For a vendor bill the buyer is "us" (the seller org); for a sales invoice
  // it's the customer (the party). Mirrors HeaderLeft's name selection.
  const displayName = isVendor ? invoice.sellerName : invoice.partyName

  return (
    <div className="party-box">
      <div className="section-header">{isVendor ? 'Buyer' : 'Bill To'}</div>
      <div className="party-address">
        {displayName?.trim() && <div className="party-name">{displayName}</div>}
        {displayAddress && <AddressBlock address={displayAddress} />}
        {!isVendor && partyBillingAddress?.companyCode?.trim() && (
          <div>Company Code: {partyBillingAddress.companyCode}</div>
        )}
      </div>
      <div className="party-identifiers">
        {gstin?.trim() && (
          <div className="party-identifier">
            <span className="identifier-label-small">GSTIN:</span> {gstin}
          </div>
        )}
      </div>
    </div>
  )
}

interface ShipToBoxProps {
  invoice: Invoice
  isVendor: boolean
  isExport: boolean
  legacyFields: InvoiceLegacyFields
}

function ShipToBoxLabel({ isVendor, isExport, hasShipToLine1 }: { isVendor: boolean; isExport: boolean; hasShipToLine1: boolean }): string {
  if (isVendor) { return 'Delivery Address' }
  if (isExport) { return hasShipToLine1 ? 'Ship To' : 'Consignee' }
  return 'Ship To'
}

function pickLegacyAddress(legacyFields: InvoiceLegacyFields): CityStatePostalCountryInput & { line1?: string; line2?: string } {
  return {
    line1: legacyFields.shipToAddressLine1 ?? legacyFields.consigneeAddressLine1,
    line2: legacyFields.shipToAddressLine2 ?? legacyFields.consigneeAddressLine2,
    city: legacyFields.shipToCity ?? legacyFields.consigneeCity,
    state: legacyFields.shipToState ?? legacyFields.consigneeState,
    postalCode: legacyFields.shipToPostalCode ?? legacyFields.consigneePostalCode,
    country: legacyFields.shipToCountry ?? legacyFields.consigneeCountry,
  }
}

function LegacyShipToBlock({ legacyFields }: { legacyFields: InvoiceLegacyFields }): JSX.Element | null {
  const addr = pickLegacyAddress(legacyFields)
  if (!addr.line1) { return null }
  return (
    <>
      <div>{addr.line1}</div>
      {addr.line2 && <div>{addr.line2}</div>}
      {shouldRenderCityStateLine(addr) && <div>{buildCityStateLine(addr)}</div>}
    </>
  )
}

function ShipToBox({ invoice, isVendor, isExport, legacyFields }: ShipToBoxProps): JSX.Element {
  const shipTo = invoice.shipToAddress
  const consignee = invoice.consigneeAddress
  const hasShipToLine1 = Boolean(shipTo?.line1)
  const label = ShipToBoxLabel({ isVendor, isExport, hasShipToLine1 })

  return (
    <div className="party-box">
      <div className="section-header">{label}</div>
      <div className="party-address">
        {hasShipToLine1 && shipTo && <AddressBlock address={shipTo} />}
        {!hasShipToLine1 && consignee?.line1 && isExport && (
          <AddressBlock address={consignee} />
        )}
        {!hasShipToLine1 && !consignee?.line1 && (
          <LegacyShipToBlock legacyFields={legacyFields} />
        )}
      </div>
      {shipTo?.gstin?.trim() && (
        <div className="party-gstin">
          <span className="gstin-label">GSTIN:</span> {shipTo.gstin}
        </div>
      )}
    </div>
  )
}

interface InvoiceViewAddressesProps {
  invoice: Invoice
  isVendor: boolean
  isExport: boolean
  sellerAddress: Address | null
  partyBillingAddress: Address | null
  legacyFields: InvoiceLegacyFields
}

export function InvoiceViewAddresses({
  invoice,
  isVendor,
  isExport,
  sellerAddress,
  partyBillingAddress,
  legacyFields,
}: InvoiceViewAddressesProps): JSX.Element {
  const shouldShowShipTo =
    invoice.shipToAddress?.line1 !== undefined ||
    invoice.consigneeAddress?.line1 !== undefined ||
    legacyFields.shipToAddressLine1 !== undefined ||
    legacyFields.consigneeAddressLine1 !== undefined

  return (
    <div className="party-section">
      <BillToBox
        invoice={invoice}
        isVendor={isVendor}
        partyBillingAddress={partyBillingAddress}
        sellerAddress={sellerAddress}
      />
      {shouldShowShipTo && (
        <ShipToBox
          invoice={invoice}
          isExport={isExport}
          isVendor={isVendor}
          legacyFields={legacyFields}
        />
      )}
    </div>
  )
}
